// -----------------------------------------------------------------------------
// LoadingScreen — Phase 14.5
//
// Cinematic ALABAMA ident loader: full-viewport black screen with a small
// centered uppercase wordmark whose letters softly illuminate one by one
// from left to right.  Replaces the previous bone-background dot-grid
// loader.  Mounted once at the app root (App.tsx, outside BrowserRouter)
// so it appears on initial page load only — internal route navigation
// does not replay it.
//
// Visual:
//   · pure black canvas
//   · single small ALABAMA wordmark, Instrument Serif, uppercase, wide
//     0.45em tracking, soft bone color, very restrained warm glow
//   · letters start near-invisible (opacity 0.06), illuminate to ~0.92
//     in a left-to-right cascade with ~95 ms stagger
//
// Timing budget (motion-on, desktop and mobile):
//   · per-letter stagger: 75 ms × 6 gaps = 450 ms (last letter starts)
//   · per-letter fade-in duration: 320 ms (last letter fully lit at 770 ms)
//   · brief hold once the full wordmark is lit: 150 ms
//   · MIN_VISIBLE_MS  = 450 + 320 + 150 = 920 ms before exit begins
//   · exit fade-out (EXIT_MS): 240 ms
//   · total fast-load visible lifecycle (incl. exit): ~1160 ms ≈ 1.16 s
//
// Hard cap:
//   · HARD_MAX_VISIBLE_MS = 1500 ms — the overlay MUST be fully gone
//     by this point (so the cap on when exit may begin is
//     LATEST_EXIT_START_MS = HARD_MAX_VISIBLE_MS − EXIT_MS = 1260 ms).
//
// Readiness:
//   · dismiss once both `document.readyState === "complete"` AND
//     min-visible time have been reached, whichever is later
//   · forced exit at LATEST_EXIT_START_MS regardless, so a slow asset
//     never traps the viewer behind the loader
//   · no querySelector("video") dependency, no route-specific logic
//
// Reduced motion:
//   · respect `prefers-reduced-motion`: the whole wordmark fades in
//     together (no per-letter cascade), then the loader fades out
//   · same MIN_VISIBLE_MS so the lifecycle remains in the 1.0–1.3 s band
//
// Side effects: none.  No body scroll lock, no listeners left behind,
// no DOM mutation outside the component's own root.
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const LETTERS = ["A", "L", "A", "B", "A", "M", "A"] as const;
const STAGGER_MS = 75;
const LETTER_DURATION_MS = 320;
const HOLD_MS = 150;
const EXIT_MS = 240;
// Min visible covers the full illumination cascade plus the hold beat.
const MIN_VISIBLE_MS = STAGGER_MS * (LETTERS.length - 1) + LETTER_DURATION_MS + HOLD_MS;
// Hard cap on the TOTAL visible lifecycle (overlay fully gone by this point).
const HARD_MAX_VISIBLE_MS = 1500;
// Latest moment at which exit may BEGIN so the fade still finishes by
// HARD_MAX_VISIBLE_MS.  Computed from the cap so the two constants stay
// consistent if either is tuned in future.
const LATEST_EXIT_START_MS = HARD_MAX_VISIBLE_MS - EXIT_MS;

const LETTER_DIM_OPACITY = 0.06;
const LETTER_LIT_OPACITY = 0.92;

const LETTER_GLOW =
  "0 0 10px rgba(255, 240, 220, 0.18), 0 0 22px rgba(255, 200, 140, 0.08)";

export function LoadingScreen() {
  const [visible, setVisible] = useState(true);
  const [exiting, setExiting] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    const mount = Date.now();
    let armed = true;

    const beginExit = () => {
      if (!armed) return;
      armed = false;
      const elapsed = Date.now() - mount;
      const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
      setTimeout(() => setExiting(true), wait);
    };

    if (document.readyState === "complete") {
      beginExit();
    } else {
      window.addEventListener("load", beginExit, { once: true });
    }

    // Hard cap — guarantees the overlay is fully gone by
    // HARD_MAX_VISIBLE_MS even if `load` never fires.  We schedule the
    // EXIT START (not the unmount), so the EXIT_MS fade is included in
    // the total visible budget.
    const cap = setTimeout(() => {
      armed = false;
      setExiting(true);
    }, LATEST_EXIT_START_MS);

    return () => {
      armed = false;
      window.removeEventListener("load", beginExit);
      clearTimeout(cap);
    };
  }, []);

  // Unmount fully after the exit fade has completed.
  useEffect(() => {
    if (!exiting) return;
    const t = setTimeout(() => setVisible(false), EXIT_MS);
    return () => clearTimeout(t);
  }, [exiting]);

  if (!visible) return null;

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: exiting ? 0 : 1 }}
      transition={{ duration: EXIT_MS / 1000, ease: "easeOut" }}
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999, backgroundColor: "#000" }}
      aria-hidden="true"
      role="presentation"
    >
      <div
        className="select-none"
        style={{
          fontFamily:
            "var(--font-display, 'Instrument Serif', 'Lora', Georgia, serif)",
          fontWeight: 400,
          fontSize: "clamp(15px, 1.2vw, 18px)",
          letterSpacing: "0.45em",
          textTransform: "uppercase",
          color: "#F4F1EA",
          // Compensate for trailing letter-spacing so the wordmark sits
          // optically centered.
          paddingLeft: "0.45em",
          whiteSpace: "nowrap",
        }}
      >
        {LETTERS.map((ch, i) => (
          <motion.span
            key={i}
            style={{
              display: "inline-block",
              textShadow: LETTER_GLOW,
            }}
            initial={
              reduced ? { opacity: LETTER_DIM_OPACITY * 4 } : { opacity: LETTER_DIM_OPACITY }
            }
            animate={{ opacity: LETTER_LIT_OPACITY }}
            transition={
              reduced
                ? { duration: 0.45, delay: 0.1, ease: "easeOut" }
                : {
                    duration: LETTER_DURATION_MS / 1000,
                    delay: (i * STAGGER_MS) / 1000,
                    ease: [0.22, 1, 0.36, 1],
                  }
            }
          >
            {ch}
          </motion.span>
        ))}
      </div>
    </motion.div>
  );
}
